import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import type { Advisor, Company, Paginated, Project } from '../../types/admin';
import type {
  ConsultaAdminParams,
  ConsultaPropiasParams,
  SeparacionDetalle,
  SeparacionResumen,
} from '../../types/consultas';

export type AlcanceConsulta = 'propias' | 'admin';

/** Tamaño de página máximo del contrato (`pageSize` 1..100). */
const CATALOGO_PAGE_SIZE = 100;

export const consultasKeys = {
  todas: ['consultas'] as const,
  propias: (params: ConsultaPropiasParams) => ['consultas', 'propias', params] as const,
  admin: (params: ConsultaAdminParams) => ['consultas', 'admin', params] as const,
  detalle: (alcance: AlcanceConsulta, id: string) => ['consultas', 'detalle', alcance, id] as const,
};

export function useMySeparations(params: ConsultaPropiasParams) {
  return useQuery<Paginated<SeparacionResumen>>({
    queryKey: consultasKeys.propias(params),
    queryFn: () => apiClient.separationQueries.mine(params),
    placeholderData: keepPreviousData,
  });
}

export function useAdminSeparations(params: ConsultaAdminParams, enabled = true) {
  return useQuery<Paginated<SeparacionResumen>>({
    queryKey: consultasKeys.admin(params),
    queryFn: () => apiClient.separationQueries.adminList(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSeparationDetail(alcance: AlcanceConsulta, id: string) {
  return useQuery<SeparacionDetalle>({
    queryKey: consultasKeys.detalle(alcance, id),
    queryFn: () =>
      alcance === 'propias'
        ? apiClient.separationQueries.mineDetail(id)
        : apiClient.separationQueries.adminDetail(id),
    enabled: Boolean(id),
  });
}

/** Catálogos para los filtros administrativos: del tenant del token (brecha B-02). */
export function useFilterCatalogs(opciones: {
  proyectos: boolean;
  asesores: boolean;
  inmobiliarias: boolean;
}) {
  const proyectos = useQuery<Paginated<Project>>({
    queryKey: ['consultas', 'catalogo', 'proyectos'],
    queryFn: () => apiClient.projects.list({ page: 1, pageSize: CATALOGO_PAGE_SIZE }),
    enabled: opciones.proyectos,
  });
  const asesores = useQuery<Advisor[]>({
    queryKey: ['consultas', 'catalogo', 'asesores'],
    queryFn: () => apiClient.users.advisors.list(),
    enabled: opciones.asesores,
  });
  const inmobiliarias = useQuery<Paginated<Company>>({
    queryKey: ['consultas', 'catalogo', 'inmobiliarias'],
    queryFn: () => apiClient.companies.list({ page: 1, pageSize: CATALOGO_PAGE_SIZE }),
    enabled: opciones.inmobiliarias,
  });
  return {
    proyectos: proyectos.data?.data ?? [],
    asesores: asesores.data ?? [],
    inmobiliarias: inmobiliarias.data?.data ?? [],
  };
}
